import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const TL = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', minimumFractionDigits: 2,
}).format(Number(value) || 0);

const formatDate = (value) => value
  ? new Date(String(value).slice(0, 10) + 'T00:00:00').toLocaleDateString('tr-TR')
  : '-';

export default function OdemeHatirlatmalariPage() {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('bugun');
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/muhasebe/odeme-hatirlatmalari');
      if (response.data.success) setData(response.data.data);
      else toast.error(response.data.message || 'Ödeme listesi alınamadı.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ödeme listesi alınamadı.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = activeTab === 'bugun' ? (data?.due_today || []) : (data?.overdue || []);
  const sendableIds = useMemo(() => rows
    .filter((row) => row.telefon)
    .map((row) => Number(row.taksit_id)), [rows]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = sendableIds.length > 0 && sendableIds.every((id) => selectedSet.has(id));

  const toggleRow = (id) => {
    setSelectedIds((current) => current.includes(id)
      ? current.filter((selectedId) => selectedId !== id)
      : [...current, id]);
  };

  const toggleAll = () => {
    setSelectedIds((current) => {
      const visible = new Set(sendableIds);
      return sendableIds.every((id) => current.includes(id))
        ? current.filter((id) => !visible.has(id))
        : [...new Set([...current, ...sendableIds])];
    });
  };

  const sendReminders = async () => {
    if (!selectedIds.length) {
      toast.error('Önce hatırlatma gönderilecek kişileri seçin.');
      return;
    }
    setSending(true);
    try {
      const response = await axios.post('/muhasebe/odeme-hatirlatmalari/gonder', {
        taksit_ids: selectedIds,
      });
      const queued = Number(response.data?.data?.queued || 0);
      const skipped = Number(response.data?.data?.skipped || 0);
      if (queued) toast.success(`${queued} ödeme hatırlatması WhatsApp kuyruğuna eklendi.`);
      if (skipped) toast.error(`${skipped} kayıt gönderilemedi. Telefonu veya ödeme durumunu kontrol edin.`);
      setSelectedIds([]);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Ödeme hatırlatmaları gönderilemedi.');
    } finally {
      setSending(false);
    }
  };

  const summary = data?.summary || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-ui)', fontWeight: 900, fontSize: '1.4rem', margin: 0 }}>
            💬 Ödeme Hatırlatmaları
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: 5 }}>
            Ödeme günü gelen ve geciken taksitleri inceleyip hatırlatmayı elle gönderin.
          </p>
        </div>
        <button className="btn" onClick={load} disabled={loading || sending}>↻ Yenile</button>
      </div>

      <div style={{ padding: '12px 15px', borderRadius: 10, background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
        WhatsApp hatırlatmaları otomatik gönderilmez. Mesajlar yalnızca seçip gönder düğmesine bastığınızda kuyruğa alınır.
      </div>

      <div className="muh-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="stat-card">
          <div className="stat-number" style={{ color: '#d97706' }}>{summary.due_today_count || 0}</div>
          <div className="stat-label">Bugün vadesi gelen · {TL(summary.due_today_amount)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-number" style={{ color: '#dc2626' }}>{summary.overdue_count || 0}</div>
          <div className="stat-label">Ödemesi geciken · {TL(summary.overdue_amount)}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { id: 'bugun', label: `Bugün vadesi gelen (${summary.due_today_count || 0})` },
          { id: 'gecikti', label: `Ödemesi geciken (${summary.overdue_count || 0})` },
        ].map((tab) => (
          <button
            key={tab.id}
            className="btn"
            onClick={() => { setActiveTab(tab.id); setSelectedIds([]); }}
            style={{
              background: activeTab === tab.id ? 'var(--blue)' : 'var(--bg-elevated)',
              color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
              border: `1px solid ${activeTab === tab.id ? 'var(--blue)' : 'var(--border)'}`,
            }}
          >{tab.label}</button>
        ))}
        <button className="btn btn-primary" onClick={sendReminders} disabled={sending || !selectedIds.length} style={{ marginLeft: 'auto' }}>
          {sending ? 'Kuyruğa ekleniyor…' : `Seçilenlere hatırlatma gönder${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="page-loader" style={{ minHeight: 220 }}><div className="spinner" /></div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 850 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-muted)', fontSize: '0.78rem', background: 'var(--bg-elevated)' }}>
                  <th style={{ padding: '12px 14px' }}><input type="checkbox" aria-label="Listedeki tüm kişileri seç" checked={allSelected} onChange={toggleAll} /></th>
                  <th style={{ padding: '12px 10px' }}>Öğrenci / Veli</th>
                  <th style={{ padding: '12px 10px' }}>Sınıf</th>
                  <th style={{ padding: '12px 10px' }}>Vade</th>
                  <th style={{ padding: '12px 10px' }}>Gecikme</th>
                  <th style={{ padding: '12px 10px' }}>Kalan tutar</th>
                  <th style={{ padding: '12px 10px' }}>Telefon</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const id = Number(row.taksit_id);
                  return (
                    <tr key={id} style={{ borderTop: '1px solid var(--border)', opacity: row.telefon ? 1 : 0.65 }}>
                      <td style={{ padding: '12px 14px' }}>
                        <input type="checkbox" aria-label={`${row.ad} ${row.soyad} seç`} disabled={!row.telefon} checked={selectedSet.has(id)} onChange={() => toggleRow(id)} />
                      </td>
                      <td style={{ padding: '12px 10px' }}>
                        <div style={{ fontWeight: 750 }}>{row.ad} {row.soyad}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{row.veli_adi || 'Veli bilgisi yok'} · {row.odeme_tipi === 'senet' ? 'Senet' : `${row.taksit_no}. taksit`}</div>
                      </td>
                      <td style={{ padding: '12px 10px' }}>{row.sinif_adi || '-'}</td>
                      <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>{formatDate(row.vade_tarihi)}</td>
                      <td style={{ padding: '12px 10px', color: row.hatirlatma_durumu === 'gecikti' ? '#dc2626' : '#d97706', fontWeight: 700 }}>
                        {row.hatirlatma_durumu === 'gecikti' ? `${row.gecikme_gun} gün` : 'Bugün'}
                      </td>
                      <td style={{ padding: '12px 10px', fontWeight: 750, whiteSpace: 'nowrap' }}>{TL(row.kalan_tutar)}</td>
                      <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>{row.telefon || 'Telefon yok'}</td>
                    </tr>
                  );
                })}
                {!rows.length && (
                  <tr><td colSpan="7" style={{ padding: 34, textAlign: 'center', color: 'var(--text-muted)' }}>
                    {activeTab === 'bugun' ? 'Bugün vadesi gelen ödenmemiş taksit yok.' : 'Gecikmiş ödenmemiş taksit yok.'}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
